import { Box } from "@chakra-ui/react";
import type {
  DateIntegerDataPoint,
  ProjectGraphQuery,
  ProjectGraph as ProjectGraphType,
} from "@src/__generated__/graphql";
import { lazyround } from "@src/utils/formatters";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { getThemeColor } from "@src/utils/tailwindConfig";
import { eachDayOfInterval, format, isBefore, isSameDay } from "date-fns";
import { flatten, groupBy } from "lodash";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "react-use";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomTooltip from "./CustomTooltip";
import s from "./ProjectGraph.module.css";

const COLORS = {
  totalBudget: "var(--chakra-colors-yellow-500)",
  spentBudget: "var(--chakra-colors-teal-500)",
  spentBudgetOver: "var(--chakra-colors-red-500)",
  wip: "var(--chakra-colors-purple-500)",
};

type TProjectGraphDataKeys = keyof Pick<
  ProjectGraphType,
  "totalBudgetData" | "spentBudgetData"
>;
const ProjectGraphDataKeys: TProjectGraphDataKeys[] = [
  "totalBudgetData",
  "spentBudgetData",
];

type IGraphElement = {
  date: string;
  totalBudgetData: number;
  spentBudgetData: number;
  wipData: number;
};

const mapPropToData = <K extends TProjectGraphDataKeys>(
  value: DateIntegerDataPoint,
  prop: K,
) => ({
  date: value.date,
  [prop]: value.amount,
});

const groupByAndFill = (
  rawData: ProjectGraphQuery["projectGraph"],
  intervalStart: Date,
  intervalEnd: Date,
) => {
  /**
   *
   * Transform elements so they can be displayed in the graph
   * Only active labels will be added
   *
   * from
   *
   *    date: 2020-02-13,
   *    amount: 13100
   *
   * to
   *
   *    date: 2020-02-13,
   *    totalBudgetData: 420,
   *    spentBudgetData: 420,
   *    wipData: 420
   *
   */
  /*
  const mappedData = flatten(
    RawDataKeys.reduce<IElement[]>((acc, label) => {
      const d = rawData[label].map(i => mapPropToData(i, label))
      acc.push(...d)

      return acc
    }, []),
  )
  */

  const mappedData = ProjectGraphDataKeys.flatMap((key) =>
    rawData[key].map((i) => mapPropToData(i, key)),
  ) as IGraphElement[];

  // Group data by date
  const groupedData = flatten(
    Object.values(groupBy(mappedData, (e) => e.date)),
  );

  // Create interval <start_date, finish_date || today>
  const intervalFull = eachDayOfInterval({
    start: intervalStart,
    end: isBefore(intervalEnd, intervalStart) ? intervalStart : intervalEnd,
  });

  // Initialize empty maximum values for each active label
  let activeMaxes = ProjectGraphDataKeys.reduce<
    Record<TProjectGraphDataKeys, number>
  >((acc, key) => {
    acc[key] = 0;

    return acc;
  }, {} as IGraphElement);

  // Iterate through interval and substitute existing data
  const data = intervalFull.map((date) => {
    const matches = groupedData.filter((el) =>
      isSameDay(new Date(el.date), date),
    );

    // There are records for this date
    if (matches.length) {
      let merged = activeMaxes as IGraphElement;
      matches.forEach((match) => {
        merged = { ...merged, ...match };
      });
      activeMaxes = merged;
      return merged;
    }

    // No record found
    return ProjectGraphDataKeys.reduce(
      (acc, key) => {
        acc[key] = activeMaxes[key];

        return acc;
      },
      { date: format(date, "yyyy-MM-dd") } as IGraphElement,
    );
  });

  return data;
};

const XTick = ({ x, y, payload }: { x: number; y: number; payload: any }) => {
  const formattedDate = format(new Date(payload.value), "MMM yyyy");
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        fill={getThemeColor("darkGray")}
        textAnchor="middle"
        x={0}
        y={0}
        dy={24}
      >
        {formattedDate}
      </text>
    </g>
  );
};

const YTick = ({ x, y, payload }: { x: number; y: number; payload: any }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        fill={getThemeColor("darkGray")}
        textAnchor="left"
        x={0}
        y={0}
        dx={-56}
      >
        {lazyround(payload.value)}
      </text>
    </g>
  );
};

const useTransformToGraphData = (
  data: ProjectGraphQuery["projectGraph"],
  interval: { start: Date; end: Date },
) =>
  useMemo(
    () => groupByAndFill(data, interval.start, interval.end),
    [data, interval.start, interval.end],
  );

const useGenerateSpentBudgetGradientValues = (graphData: IGraphElement[]) =>
  useMemo(() => {
    let parts: { offset: number; color: string }[] = [];
    let lastState: "under" | "over" | undefined;

    if (!graphData.length) return parts;

    const calcOffsetFromIndex = (index: number): number =>
      index === 0 ? 0 : ((index + 1) * 100) / graphData.length;

    const compare = (data: IGraphElement): "under" | "over" =>
      data.spentBudgetData > data.totalBudgetData ? "over" : "under";

    // Init first part
    if (compare(graphData[0]) === "over") {
      lastState = "over";
      parts = [{ offset: 0, color: COLORS.spentBudgetOver! }];
    } else {
      lastState = "under";
      parts = [{ offset: 0, color: COLORS.spentBudget! }];
    }

    // Generate other gradients
    for (let i = 1; i < graphData.length; i++) {
      const data = graphData[i];
      let offset: number;

      const currentState = compare(data);

      if (currentState === "over" && lastState === "under") {
        lastState = currentState;
        offset = calcOffsetFromIndex(i - 1);
        parts.push({ offset, color: COLORS.spentBudget! }); // close last
        parts.push({ offset, color: COLORS.spentBudgetOver! }); // open new
      } else if (currentState === "under" && lastState === "over") {
        lastState = currentState;
        offset = calcOffsetFromIndex(i - 1);
        parts.push({ offset, color: COLORS.spentBudgetOver! }); // close last
        parts.push({ offset, color: COLORS.spentBudget! }); // open new
      }
    }

    return parts;
  }, [graphData]);

const PROJECT_GRAPH_WRAPPER_ID = "project-graph";

interface ProjectGraphProps {
  data: ProjectGraphQuery["projectGraph"];
  intervalStart: Date;
  intervalEnd: Date;
  isTimeAndMaterials: boolean;
  totalBudgetCheckbox: boolean;
  trackedBudgetCheckbox: boolean;
}

const ProjectGraph: FunctionComponent<ProjectGraphProps> = ({
  data,
  intervalStart,
  intervalEnd,
  totalBudgetCheckbox,
  isTimeAndMaterials,
  trackedBudgetCheckbox,
}) => {
  const { width } = useWindowSize();
  const { isDesktop } = useScreenType();
  const [graphDimensions, setGraphDimensions] = useState<{
    width?: number;
    height?: number;
  }>({
    width: undefined,
    height: undefined,
  });

  const transformedData = useTransformToGraphData(data, {
    start: intervalStart,
    end: intervalEnd,
  });
  const spentBudgetGradientStopValues =
    useGenerateSpentBudgetGradientValues(transformedData);

  const tooltipX = useMemo(() => {
    return isDesktop ? (graphDimensions.width ?? 0) - 405 : 0;
  }, [graphDimensions.width]);

  const tooltipY = useMemo(() => {
    const yOffset = (graphDimensions.height ?? 0) >= 405 ? 190 : 170;

    return (graphDimensions.height ?? 0) - yOffset;
  }, [graphDimensions.width]);

  useEffect(() => {
    const graph = document.getElementById(PROJECT_GRAPH_WRAPPER_ID);

    if (!graph) return;

    const { offsetWidth, offsetHeight } = graph;

    setGraphDimensions(({ width, height }) => ({
      width: width === offsetWidth ? width : offsetWidth,
      height: height === offsetHeight ? height : offsetHeight,
    }));
  }, [width]);

  if (!transformedData) return null;

  return (
    <Box className={s.PageGraph} mt="8" id={PROJECT_GRAPH_WRAPPER_ID}>
      <ResponsiveContainer width="100%" height={isDesktop ? 240 : 160}>
        <LineChart data={transformedData}>
          <CartesianGrid stroke={getThemeColor("lightGray")} />

          <XAxis dataKey="date" tick={XTick as any} ticks={[]} />
          <YAxis domain={["dataMin", "dataMax"]} tick={YTick as any} />

          <Tooltip
            content={<CustomTooltip isTimeAndMaterials={isTimeAndMaterials} />}
            position={{ x: tooltipX, y: tooltipY }}
          />

          <defs>
            <linearGradient
              id="spentBudgetGradient"
              x1="0%"
              x2="100%"
              y1="0"
              y2="0"
            >
              {spentBudgetGradientStopValues.map(({ offset, color }) => (
                <stop
                  key={`${offset}-${color}`}
                  offset={`${offset}%`}
                  stopColor={color}
                />
              ))}
            </linearGradient>
          </defs>

          {/* Line: TotalBudget */}
          {!isTimeAndMaterials && (
            <Line
              dataKey={totalBudgetCheckbox ? "totalBudgetData" : ""}
              stroke={COLORS.totalBudget}
              strokeWidth={3}
              dot={false}
            />
          )}

          {/* Line: SpentBudget */}
          <Line
            dataKey={trackedBudgetCheckbox ? "spentBudgetData" : ""}
            stroke={
              isTimeAndMaterials
                ? COLORS.totalBudget
                : totalBudgetCheckbox
                  ? "url(#spentBudgetGradient)"
                  : COLORS.spentBudget
            }
            strokeWidth={3}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default observer(ProjectGraph);
